import {FocusMonitor} from '@angular/cdk/a11y';
import {coerceBooleanProperty} from '@angular/cdk/coercion';
import {Component, ElementRef, Input, OnDestroy, Optional, Self} from '@angular/core';
import {FormBuilder, FormGroup, ControlValueAccessor, NgControl, Validators} from '@angular/forms';
import {MatFormFieldControl} from '@angular/material/form-field';
import {Subject} from 'rxjs';

/** Data structure for holding LatLang values. */
export class LatLang {
  constructor(public degree: number, public minutes: number, public seconds?: number) {}
}
@Component({
  selector: 'app-latlong-input',
  templateUrl: './latlong-input.component.html',
  styleUrls: ['./latlong-input.component.scss'],
  providers: [{provide: MatFormFieldControl, useExisting: LatlongInputComponent}],
  host: {
    '[class.latlang-floating]': 'shouldLabelFloat',
    '[id]': 'id',
    '[attr.aria-describedby]': 'describedBy',
  }
})

export class LatlongInputComponent implements ControlValueAccessor, MatFormFieldControl<LatLang>, OnDestroy {
  static nextId = 0;

  parts: FormGroup;
  stateChanges = new Subject<void>();
  focused = false;
  id = `latLang-input-${LatlongInputComponent.nextId++}`;
  describedBy = '';
  onChange = (_: any) => {};
  onTouched = () => {};

  get errorState() {
    return this.ngControl.errors !== null && !!this.ngControl.touched;
  }
  get empty() {
    const {value: {degree, minutes, seconds}} = this.parts;

    return !degree && !minutes && !seconds;
  }

  get shouldLabelFloat() { return this.focused || !this.empty; }

  @Input()
  get placeholder(): string { return this._placeholder; }
  set placeholder(value: string) {
    this._placeholder = value;
    this.stateChanges.next();
  }
  private _placeholder: string;

  @Input()
  get required(): boolean { return this._required; }
  set required(value: boolean) {
    this._required = coerceBooleanProperty(value);
    this.stateChanges.next();
  }
  private _required = false;

  @Input() coordinateType : string
  
  @Input()
  get disabled(): boolean { return this._disabled; }
  set disabled(value: boolean) {
    this._disabled = coerceBooleanProperty(value);
    this._disabled ? this.parts.disable() : this.parts.enable();
    this.stateChanges.next();
  }
  private _disabled = false;

  @Input()
  get value(): LatLang | null {
    if (this.parts.valid) {
      const {value: {degree, minutes, seconds}} = this.parts;
      return new LatLang(degree, minutes, seconds);
    }
    return null;
  }
  set value(latLang: LatLang | null) {
    const {degree, minutes, seconds} = latLang || new LatLang(NaN ,NaN , NaN);
    this.parts.setValue({degree, minutes, seconds});
    this.onChange({degree, minutes, seconds});
    this.stateChanges.next();
  }

  constructor(
    formBuilder: FormBuilder,
    private _focusMonitor: FocusMonitor,
    private _elementRef: ElementRef<HTMLElement>,
    @Optional() @Self() public ngControl: NgControl) {

    this.parts = formBuilder.group({
      degree: [null, [Validators.required]],
      minutes: [null, [Validators.required]],
      seconds: [null, [Validators.required]],
    });

    _focusMonitor.monitor(_elementRef, true).subscribe(origin => {
      if (this.focused && !origin) {
        this.onTouched();
      }
      this.focused = !!origin;
      this.stateChanges.next();
    });

    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnDestroy() {
    this.stateChanges.complete();
    this._focusMonitor.stopMonitoring(this._elementRef);
  }

  setDescribedByIds(ids: string[]) {
    this.describedBy = ids.join(' ');
  }

  onContainerClick(event: MouseEvent) {
    if ((event.target as Element).tagName.toLowerCase() != 'input') {
      this._elementRef.nativeElement.querySelector('input')!.focus();
    }
  }

  writeValue(latlang: LatLang | null): void {
    this.value = latlang;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  _handleInput(): void {
    this.onChange(this.value);
  }

}
